import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import AddClientModal from '../components/AddClientModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import Footer from '../components/Footer';
import { showAppAlert } from '../utils/alertService';
import LoaderOverlay from '../components/LoaderOverlay';
import '../styles/formStyles.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const API_URL = process.env.REACT_APP_API_BASE_URL;

function Login() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    email: '',
    senha: '',
  });
  const [addClientModalVisible, setAddClientModalVisible] = useState(false);
  const [forgotPasswordModalVisible, setForgotPasswordModalVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);

  // Novo estado para seleção de empresa
  const [selectCompanyModal, setSelectCompanyModal] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  // Adiciona verificação de ambiente para desativar o reCAPTCHA
  const isRecaptchaBypassed =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'hml.fidelitystore.com.br' ||
    window.location.hostname === 'http://hml.fidelitystore.com.br';

  // Estado para reCAPTCHA
  const [recaptchaToken, setRecaptchaToken] = useState(isRecaptchaBypassed ? 'localhost-bypass' : '');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleAddClientModalClose = () => {
    setAddClientModalVisible(false);
  };

  const handleClientAdded = () => {
    showAppAlert('success', 'Cliente cadastrado com sucesso!');
    setAddClientModalVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!recaptchaToken) {
      showAppAlert('warning', 'Por favor, complete o reCAPTCHA.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.senha,
          recaptcha_token: recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);

        // Para clientes, pegue o client_id do primeiro objeto em companies (se existir)
        let clientId = data.client_id;
        if (
          data.perfil === 'cliente' &&
          Array.isArray(data.companies) &&
          data.companies.length > 0 &&
          data.companies[0].client_id
        ) {
          clientId = data.companies[0].client_id;
        }

        const shouldDelayClientId =
          data.perfil === 'cliente' &&
          Array.isArray(data.companies) &&
          data.companies.length > 1;

        localStorage.setItem(
          'user',
          JSON.stringify({
            id: data.id,
            client_id: shouldDelayClientId ? undefined : clientId,
            email: credentials.email,
            name: data.name,
            perfil: data.perfil,
            primeiroAcesso: data.primeiroAcesso,
          })
        );

        if (Array.isArray(data.companies)) {
          localStorage.setItem('companies', JSON.stringify(data.companies));
          if (data.companies.length > 0) {
            localStorage.setItem('company_id', data.companies[0].id);
            localStorage.setItem('urlLogo', data.companies[0].urlLogo || '');
            localStorage.setItem('nomeFantasia', data.companies[0].nomeFantasia || '');
          }
        }
        if (data.qtd_companies !== undefined) {
          localStorage.setItem('qtd_companies', data.qtd_companies);
        }

        // Se for lojista e houver mais de uma empresa, exibe modal de seleção
        if (
          data.perfil === 'lojista' &&
          Array.isArray(data.companies) &&
          data.companies.length > 1
        ) {
          setCompanyOptions(data.companies);
          setSelectedCompanyId("");
          setLoading(false);
          setSelectCompanyModal(true);
          return;
        }

        // Se for cliente e houver mais de uma empresa, exibe modal de seleção
        if (
          data.perfil === 'cliente' &&
          Array.isArray(data.companies) &&
          data.companies.length > 1
        ) {
          setCompanyOptions(data.companies);
          setSelectedCompanyId("");
          setLoading(false);
          setSelectCompanyModal(true);
          return;
        }

        if (data.perfil === 'lojista') {
          navigate('/storeowner');
        } else if (data.perfil === 'adm') {
          navigate('/adm');
        } else {
          navigate('/client');
        }
      } else {
        showAppAlert('error', data.message || 'Erro ao fazer login. Verifique suas credenciais.');
        if (!isRecaptchaBypassed) {
          setRecaptchaToken('');
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
        }
        setLoading(false);
      }
    } catch (error) {
      showAppAlert('error', 'Erro ao fazer login. Tente novamente mais tarde.');
      if (!isRecaptchaBypassed) {
        setRecaptchaToken('');
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LoaderOverlay isLoading={loading} message="Entrando..." />
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="form-container">
        <img src="/logo-fidelity.png" alt="Logotipo" />
        <h2>Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="label-style">
            <label>
              E-mail:
              <input
                type="email"
                name="email"
                value={credentials.email}
                onChange={handleInputChange}
                required
                autoComplete="email"
                className="input-style input-medium"
              />
            </label>
          </div>
          <div className="label-style">
            <label>
              Senha:
              <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="senha"
                  value={credentials.senha}
                  onChange={handleInputChange}
                  required
                  autoComplete="current-password"
                  className="input-style input-medium"
                />
                <span
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  onTouchStart={() => setShowPassword(true)}
                  onTouchEnd={() => setShowPassword(false)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8A2BE2"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </span>
              </span>
            </label>
          </div>

          {/* reCAPTCHA */}
          {!isRecaptchaBypassed && (
            <div className="label-style" style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onExpired={() => setRecaptchaToken('')}
                onError={() => setRecaptchaToken('')}
              />
            </div>
          )}

          <button 
            type="submit" 
            className="button-style"
            disabled={!recaptchaToken || loading}
            style={{
              opacity: (recaptchaToken && !loading) ? 1 : 0.6,
              cursor: (recaptchaToken && !loading) ? 'pointer' : 'not-allowed'
            }}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <p style={{ marginTop: '15px', textAlign: 'center' }}>
          <span
            style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setForgotPasswordModalVisible(true)}
          >
            Esqueci a senha
          </span>
        </p>
      </div>
      </div>

      {/* Modal de seleção de empresa */}
      {selectCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 350 }}>
            <h3>Selecione a empresa para acessar o painel</h3>
            <select
              value={selectedCompanyId}
              onChange={e => setSelectedCompanyId(e.target.value)}
              className="input-style"
              style={{ width: '100%', margin: '20px 0' }}
            >
              <option value="">Selecione</option>
              {companyOptions.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.nomeFantasia}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="button-style"
                onClick={() => {
                  const selected = companyOptions.find(c => String(c.id) === String(selectedCompanyId));
                  if (selected) {
                    localStorage.setItem('company_id', selected.id);
                    localStorage.setItem('urlLogo', selected.urlLogo || '');
                    localStorage.setItem('nomeFantasia', selected.nomeFantasia || '');
                    const user = JSON.parse(localStorage.getItem('user'));
                    user.client_id = selected.client_id;
                    localStorage.setItem('user', JSON.stringify(user));
                  }
                  const { perfil } = JSON.parse(localStorage.getItem('user'));
                  if (perfil === 'lojista') {
                    navigate('/storeowner');
                  } else {
                    navigate('/client');
                  }
                }}
                disabled={!selectedCompanyId}
              >
                Confirmar
              </button>
              <button
                className="button-style cancel"
                onClick={() => setSelectCompanyModal(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <AddClientModal
        isOpen={addClientModalVisible}
        onClose={handleAddClientModalClose}
        onClientAdded={handleClientAdded}
      />

      <ForgotPasswordModal
        isOpen={forgotPasswordModalVisible}
        onClose={() => setForgotPasswordModalVisible(false)}
      />
      <Footer />
    </div>
  );
}

export default Login;